import { useEffect, useMemo, useRef, useState } from 'react';
import { isApiError } from '../api';
import { vehicles as vehiclesApi } from '../api/vehicles';
import { drawingFor, useVehicleTypes } from './useVehicleTypes';

/** How often the home asks: the riders' own positions arrive about this often. */
export const NEARBY_POLL_MS = 5000;

/**
 * The free riders near a point, asked every five seconds while the screen
 * is in view (InGo ADR 0036; Kangaru's live map: nothing is asked while the
 * tab is hidden, and the map repaints at once on return). A failed ask keeps
 * the last answer: an empty map reads as "nobody is out". Stops for good on
 * a 401 (signed out) and when the screen goes away.
 *
 * `point` is `{ lat, lng }` or null (nothing asked until there is one).
 * Returns the vehicles as the map draws them: `{ key, vehicle, state, lat,
 * lng, heading, at }`, `at` being when the position was reported, and
 * `vehicle` the drawing the office chose for the rider's vehicle type.
 */
export function useNearbyRiders(point, { nearby, pollMs = NEARBY_POLL_MS, vehicleTypes = vehiclesApi } = {}) {
  const { vehicles: types } = useVehicleTypes(vehicleTypes);
  const [vehicles, setVehicles] = useState([]);
  const signedOut = useRef(false);
  // Rounded to about 100 m, so a client's small moves do not restart the asking.
  const lat = point?.lat == null ? undefined : Math.round(point.lat * 1000) / 1000;
  const lng = point?.lng == null ? undefined : Math.round(point.lng * 1000) / 1000;

  useEffect(() => {
    if (!nearby || lat == null || lng == null || signedOut.current) return undefined;
    let cancelled = false;
    let timer = 0;

    const ask = async () => {
      if (cancelled || signedOut.current || document.visibilityState === 'hidden') return;
      try {
        const rows = await nearby.around(lat, lng);
        if (cancelled) return;
        const now = Date.now();
        setVehicles(
          rows.map((row) => ({
            key: row.key,
            vehicle: row.vehicle,
            state: 'free',
            lat: row.latitude,
            lng: row.longitude,
            heading: Number.isFinite(row.heading_degrees) ? row.heading_degrees : null,
            at: now - (Number(row.age_seconds) || 0) * 1000,
          })),
        );
      } catch (err) {
        if (isApiError(err) && err.status === 401) signedOut.current = true;
        // Otherwise the last answer stands until the next one.
      }
    };

    const loop = () => {
      window.clearInterval(timer);
      timer = window.setInterval(ask, pollMs);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void ask();
        loop();
      } else {
        window.clearInterval(timer);
      }
    };

    void ask();
    loop();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [nearby, lat, lng, pollMs]);

  // The API names the rider's vehicle type; the map draws the type's drawing.
  return useMemo(() => vehicles.map((v) => ({ ...v, vehicle: drawingFor(v.vehicle, types) })), [vehicles, types]);
}
